import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { fetchStudentPaymentStatuses } from '../services/admin-metrics.service';
import {
  formatDate,
  paymentBadgeClass,
  paymentDisplayStatus,
  paymentStatusLabel,
  type StudentPaymentStatus,
} from '../utils/admin-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface StudentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType?: string;
}

type TabId = 'overdue' | 'upcoming' | 'all';

export class AdminPaymentsPage implements View {
  private students: StudentUser[] = [];
  private paymentMap = new Map<string, StudentPaymentStatus>();
  private overdueList: StudentUser[] = [];
  private activeTab: TabId = 'overdue';
  private loading = true;
  private errorMsg: string | null = null;
  private highlightId = '';

  async render(): Promise<string> {
    const params = router.getParams();
    this.highlightId = params.highlight ?? '';
    if (params.tab === 'upcoming' || params.tab === 'overdue' || params.tab === 'all') {
      this.activeTab = params.tab;
    }

    await this.loadData();

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-payments-loading"><p>Cargando pagos...</p></div>`;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-payments-error">
          <h3>Error al cargar pagos</h3>
          <p>${this.errorMsg}</p>
        </div>
      `;
    }

    const upcoming = this.getUpcomingStudents();
    const tabRows =
      this.activeTab === 'overdue'
        ? this.overdueList
        : this.activeTab === 'upcoming'
          ? upcoming
          : this.students;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 data-testid="admin-payments-title">Gestión de pagos</h2>
          <p>Morosos, próximos vencimientos y estado de membresía de todos los alumnos.</p>
        </div>
      </div>

      <div class="admin-tabs" data-testid="admin-payments-tabs">
        <button type="button" class="admin-tab ${this.activeTab === 'overdue' ? 'active' : ''}" data-tab="overdue">
          ${getTyrisIcon('alerta', { size: 16 })} Morosos (${this.overdueList.length})
        </button>
        <button type="button" class="admin-tab ${this.activeTab === 'upcoming' ? 'active' : ''}" data-tab="upcoming">
          ${getTyrisIcon('pagos', { size: 16 })} Próximos vencimientos (${upcoming.length})
        </button>
        <button type="button" class="admin-tab ${this.activeTab === 'all' ? 'active' : ''}" data-tab="all">
          ${getTyrisIcon('socios', { size: 16 })} Todos los alumnos (${this.students.length})
        </button>
      </div>

      <div class="card-glass admin-table-wrap" data-testid="admin-payments-table">
        ${
          tabRows.length === 0
            ? `<div class="admin-empty"><p>No hay registros en esta sección.</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Alumno</th>
                <th>Estado</th>
                <th>Próximo vencimiento</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${tabRows.map((s) => this.renderPaymentRow(s)).join('')}
            </tbody>
          </table>
        `
        }
      </div>
    `;
  }

  afterRender(): void {
    document.querySelectorAll('.admin-tab[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tab = (btn as HTMLElement).getAttribute('data-tab') as TabId;
        const highlight = this.highlightId ? `&highlight=${this.highlightId}` : '';
        window.location.hash = `#/admin/payments?tab=${tab}${highlight}`;
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      });
    });

    document.querySelectorAll('[data-student-detail]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const id = (el as HTMLElement).getAttribute('data-student-detail');
        if (id) window.location.hash = `#/admin/student?id=${id}`;
      });
    });
  }

  private renderPaymentRow(s: StudentUser): string {
    const ps = this.paymentMap.get(s.id);
    const display = ps ? paymentDisplayStatus(ps) : 'NONE';
    const highlighted = s.id === this.highlightId ? ' admin-row-highlight' : '';

    return `
      <tr class="${highlighted.trim()}" data-testid="payment-row-${s.id}">
        <td>
          <div class="admin-cell-primary">${s.firstName} ${s.lastName}</div>
          <div class="admin-cell-muted">${s.email}</div>
        </td>
        <td><span class="${paymentBadgeClass(display)}">${paymentStatusLabel(display)}</span></td>
        <td>${formatDate(ps?.nextDueDate)}</td>
        <td>
          <a href="#/admin/student?id=${s.id}" class="btn btn-outline btn-sm" data-student-detail="${s.id}">
            Ver alumno →
          </a>
        </td>
      </tr>
    `;
  }

  private getUpcomingStudents(): StudentUser[] {
    return this.students.filter((s) => {
      const ps = this.paymentMap.get(s.id);
      return ps && paymentDisplayStatus(ps) === 'UPCOMING';
    });
  }

  private async loadData(): Promise<void> {
    try {
      const [students, overdueRaw] = await Promise.all([
        apiClient.get<StudentUser[]>('/api/users/students'),
        apiClient.get<
          Array<{ id: string; firstName: string; lastName: string; email: string }>
        >('/api/payments/overdue'),
      ]);

      this.students = students;
      this.paymentMap = await fetchStudentPaymentStatuses(students);

      const overdueIds = new Set(overdueRaw.map((o) => o.id));
      this.overdueList = students.filter((s) => overdueIds.has(s.id));
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar pagos';
    } finally {
      this.loading = false;
    }
  }
}
